import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import NotesPopup from './NotesPopup';
import authService from './api-authorization/AuthorizeService';

interface Reschedule {
    rescheduleItems: RescheduleItem[];
    scheduledItems: ScheduledItem[];
    showNotes: boolean;
    editNotes: EditNotes;
    errors: any;
    loading: boolean;
}

interface RescheduleProps {
    idList: number[];
    save(): void;
}

interface RescheduleItem {
    id: number;
    copyId: number;
    siteAddress: string;
    itemId: number;
    item: string;
    type: string;
    scheduleDate: string;
    onhireDate: string;
    onhireDateFormatted: string;
    offhireDate: string;
    offhireDateFormatted: string;
    quantityTotal: number;
    quantity: number;
    unitOfMeasure: string;
    price: string;
    overhireRate: string;
    priceFormatted: string;
    overhireRateFormatted: string;
    rescheduleLater: boolean;
    notes: string;
    pickupOnly: boolean;
    scheduleBranchId: number;
    warning: boolean;
    locked: boolean;
}

interface ScheduledItem {
    id: number;
    scheduledDateFormatted: string;
    status: string;
    siteAddress: string;
    itemId: number;
    item: string;
    type: string;
    onhireDate: string;
    onhireDateFormatted: string;
    quantityScheduled: string;
    unitOfMeasure: string;
    price: string;
    overhireRate: string;
    priceFormatted: string;
    overhireRateFormatted: string;
    notes: string;
    isDirty: boolean;
}

interface EditNotes {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

export default class RescheduleDetails extends React.Component<RescheduleProps, Reschedule> {
    constructor(props: RescheduleProps) {
        super(props);

        this.state = {
            rescheduleItems: [],
            scheduledItems: [],
            showNotes: false,
            editNotes: {
                heading: "",
                rowIndex: 0,
                parentIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                notes: ""
            },
            errors: {},
            loading: true
        };    
    }

    componentDidMount() {
        this.getReschedulingItems();
    }

    async getReschedulingItems() {
        const token = await authService.getAccessToken();

        //to pass an array of ids through I need to concat each of them together
        var props = "";
        for (var i = 0; i < this.props.idList.length; i++) {
            if (props != "") {
                props += "&";
            }
            props += "rescheduleIds=" + this.props.idList[i];
        }

        axios.get('api/RunSheet/GetReschedulingItems?' + props, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ rescheduleItems: res.data.rescheduleItems, scheduledItems: res.data.scheduleItems, errors: {}, loading: false })
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //related items grid item changed
    onCellChangeRelated = (itemIndex: number, item: string, value: any) => {
        //update item that has changed
        var scheduledItems = this.state.scheduledItems;
        var scheduledItem = scheduledItems[itemIndex];
        let newScheduledItem: any = {};
        newScheduledItem = scheduledItem;
        newScheduledItem[item] = value;
        newScheduledItem["isDirty"] = true;
        this.setState({ scheduledItems: scheduledItems, errors: {} });
    }

    //grid item changed
    onCellChange = (itemIndex: number, item: string, value: any) => {
        //update item that has changed
        var rescheduleItems = this.state.rescheduleItems;
        var rescheduleItem = rescheduleItems[itemIndex];
        let newRescheduledItem: any = {};
        newRescheduledItem = rescheduleItem;
        newRescheduledItem[item] = value;

        if (item == "scheduleDate") {
            this.getDateLockWarning(rescheduleItems, rescheduleItem, value);
        } else {
            this.setState({ rescheduleItems: rescheduleItems, errors: {} });
        }     
    }

    async getDateLockWarning(rescheduleItems: RescheduleItem[], rescheduleItem: RescheduleItem, scheduleDate: string) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //check to see if the date is locked or a public holiday or within a shutdown period
        axios.get('api/Schedule/CheckDateLockedWarning?ScheduleDate=' + scheduleDate + '&ScheduleBranchId=' + rescheduleItem.scheduleBranchId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var locked = res.data.locked;
            var holiday = res.data.warning;

            if (locked) {
                rescheduleItem.locked = true;
                rescheduleItem.warning = false;
                this.setState({ rescheduleItems: rescheduleItems, errors: {} });
                toast.error("The date you have selected has been locked, please select a new date or unlock before rescheduling!");
            } else if (holiday) {
                rescheduleItem.locked = false;
                rescheduleItem.warning = true;
                this.setState({ rescheduleItems: rescheduleItems, errors: {} });
            } else {
                rescheduleItem.locked = false;
                rescheduleItem.warning = false;
                this.setState({ rescheduleItems: rescheduleItems, errors: {} });
            };
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    cancelItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        //just remove from list - don't need to do anything with it
        var rescheduleItems = this.state.rescheduleItems;
        rescheduleItems.splice(itemIndex, 1);
        this.setState({ rescheduleItems: rescheduleItems, errors: {} });
    }

    handleNotesChange = (e: any) => {
        var editNotes = this.state.editNotes;
        let newNotes: any = {};
        newNotes = editNotes;
        newNotes[e.target.name] = e.target.value;

        this.setState({ editNotes: editNotes, errors: {} });
    }

    //notes
    hideNotes = () => {
        var resetNotes: EditNotes = {
            heading: "",
            rowIndex: 0,
            parentIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        }

        this.setState({ editNotes: resetNotes, showNotes: false });
    }

    editNotes = (e: any, itemIndex: number) => {
        e.preventDefault();
        var rescheduleItems = this.state.rescheduleItems;
        var rescheduleItem = rescheduleItems[itemIndex];

        var editNotes: EditNotes = {
            heading: "Edit Notes for " + rescheduleItem.item,
            rowIndex: itemIndex,
            parentIndex: 0,
            id: rescheduleItem.id,
            itemId: rescheduleItem.itemId,
            item: rescheduleItem.item,
            notes: rescheduleItem.notes ? rescheduleItem.notes : ""
        }

        this.setState({ editNotes: editNotes, showNotes: true, errors: {} });
    }

    updateNotes = (editNote: EditNotes) => {
        var editNotes = this.state.editNotes;
        editNotes.notes = editNote.notes;
        this.setState({
            editNotes: editNotes
        });
    }

    saveNotes = () => {
        this.saveRunsheetNotes();
    }

    async saveRunsheetNotes() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var editNotes = this.state.editNotes;
        var rescheduleItems = this.state.rescheduleItems;
        var rescheduleItem = rescheduleItems[editNotes.rowIndex];

        rescheduleItem.notes = editNotes.notes;

        var notes = {
            Id: rescheduleItem.id,
            Notes: editNotes.notes,
            ItemId: editNotes.itemId,
            Item: rescheduleItem.item,
            SubId: user.sub
        };

        //save
        //only if we aren't rescheduling a copy of an item.  Copy items have an 0 id as we want to insert a new row so can't be updated
        if (rescheduleItem.id > 0) {
            axios.post('api/RunSheet/SaveNotes', notes, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                toast.success("Run Sheet Notes Saved");
            })
            .catch(error => {
                toast.error(error.message);
            });
        }

        var resetNotes: EditNotes = {
            heading: "",
            rowIndex: 0,
            parentIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        }

        this.setState({ rescheduleItems: rescheduleItems, showNotes: false, editNotes: resetNotes, errors: {} });
    }
    //end notes

    validate = () => {
        let errors: any = {};
        let formIsValid = true;

        let items = this.state.rescheduleItems;
        var rowNumber = 0;

        //check if any dates are locked before validating
        var locked = items.filter(i => i.locked);
        if (locked.length > 0) {
            toast.error("You must select dates that are not locked in order to reschedule!");
            return false;
        }

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (!items[i].rescheduleLater) {
                if (items[i].scheduleDate == "") {
                    newErrorString += "Schedule Date, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(items[i].scheduleDate)) {
                    newErrorString += " Valid Schedule Date, ";
                    error = true;
                }

                if (items[i].type == "Install") {
                    if (items[i].onhireDate == "") {
                        newErrorString += "Onhire Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[i].onhireDate)) {
                        newErrorString += " Valid Onhire Date, ";
                        error = true;
                    } else if (!DateHelpers.compareDate(items[i].scheduleDate, items[i].onhireDate)) {
                        newErrorString += " Onhire Date >= Schedule Date, ";
                        error = true;
                    }
                } else if (items[i].type = "Pickup") {
                    if (items[i].offhireDate == "") {
                        newErrorString += "Offhire Date, ";
                        error = true;
                    } else if (!DateHelpers.isValidDate(items[i].offhireDate)) {
                        newErrorString += " Valid Offhire Date, ";
                        error = true;
                    } else if (!DateHelpers.compareDate(items[i].offhireDate, items[i].scheduleDate)) {
                        newErrorString += " Offhire Date <= Schedule Date, ";
                        error = true;
                    }
                }

                if (items[i].quantity < 1) {
                    newErrorString += "Quantity, ";
                    error = true;
                }
            }
           
            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errors["rescheduleItems"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    submit = (e: any) => {
        e.preventDefault();

        if (this.validate()) {
            //save
            this.saveReschedule();
        } else {
            toast.error("Please fix validation errors before rescheduling!");
        }
    }

    async saveReschedule() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let reschedule = {
            rescheduleItems: this.state.rescheduleItems,
            subId: user.sub
        };

        axios.post('api/RunSheet/SaveReschedule', reschedule, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Reschedule Saved");
            this.props.save();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    validateRelated = () => {
        let errors: any = {};
        let formIsValid = true;

        let items = this.state.scheduledItems;
        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (items[i].type == "Install") {
                if (items[i].onhireDateFormatted == "") {
                    newErrorString += "Onhire Date, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(items[i].onhireDateFormatted)) {
                    newErrorString += " Valid Onhire Date, ";
                    error = true;
                }
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errors["scheduleItems"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    save = (e: any) => {
        e.preventDefault();

        if (this.validateRelated()) {
            this.saveRescheduleRelated();
        }
    }

    async saveRescheduleRelated() {
        var itemsToSave = this.state.scheduledItems.filter(data => data.isDirty);
        var finalItems = [];
        for (var i = 0; i < itemsToSave.length; i++) {
            var item = {
                id: itemsToSave[i].id,
                itemId: itemsToSave[i].itemId,
                item: itemsToSave[i].item,
                onhireDate: itemsToSave[i].onhireDateFormatted
            };
            finalItems.push(item);
        }

        if (finalItems.length > 0) {
            const token = await authService.getAccessToken();
            const user = await authService.getUser();

            var itemList = {
                relatedItems: finalItems,
                subId: user.sub
            };

            //save
            axios.post('api/RunSheet/SaveRescheduleRelated', itemList, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                toast.success("Related Items Saved");
            })
            .catch(error => {
                toast.error(error.message);
            });
        } else {
            toast.info("No onhire dates have changed!");
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        var rescheduleItems = this.state.rescheduleItems;
        var scheduledItems = this.state.scheduledItems;
        let notesPopup = <NotesPopup showEditNotesModal={this.state.showNotes} editNotes={this.state.editNotes} hideNotes={this.hideNotes} updateNotes={this.updateNotes} saveNotes={this.saveNotes} />;
        let warnings = rescheduleItems.filter(r => r.warning || r.locked);

        return (
            <form onSubmit={this.submit}>
                <div className="static-modal">
                    {notesPopup}
                </div>
                
                <div className="halfHeight">
                    <h3>Re-schedule Items</h3>
                    <div className="table__margin reschedule-table--height overflowAuto">
                        <table className="table table__large tableColours">
                            <thead>
                                <tr>
                                    <th className="hidden">Id</th>
                                    <th></th>
                                    <th>Site Address</th>
                                    <th>Item</th>
                                    <th>Type</th>
                                    <th>Offhire Orig</th>
                                    <th>Offhire Date</th>
                                    <th>Schedule Date</th>
                                    <th>Onhire Orig</th>
                                    <th>Onhire Date</th>
                                    <th>Quantity</th>
                                    <th></th>
                                    <th>Price</th>
                                    <th>O/H Rate</th>
                                    <th>Later</th>
                                    <th>Notes</th>
                                    <th>Pickup Only</th>
                                    <th>Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rescheduleItems.map((item, index) =>
                                    <tr key={index}>
                                        <td className="hidden">{item.id}</td>
                                        <td className="table__text--align">{index + 1}</td>
                                        <td className="table__text--align">
                                            <div className="tooltipitem tooltipitem__right">{item.siteAddress ? item.siteAddress.substring(0, 20) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__right">{item.siteAddress}</span>
                                            </div>
                                        </td>
                                        <td className="table__text--align">{item.item}</td>
                                        <td className="table__text--align"> {item.type}</td>
                                        <td className="table__text--align"> {item.offhireDateFormatted}</td>
                                        <td className="table__text--align">
                                            <input type='date' id="offhireDate" name="offhireDate" className={item.type == 'Install' ? 'hidden' : 'table__text--align form-control gridscheduledatesize'} value={item.offhireDate} onChange={(e) => this.onCellChange(index, "offhireDate", e.target.value)}></input>
                                        </td>
                                        <td className="table__text--align">
                                            <input type='date' id="scheduleDate" name="scheduleDate" className='form-control gridscheduledatesize' value={item.scheduleDate} onChange={(e) => this.onCellChange(index, "scheduleDate", e.target.value)}></input>
                                        </td>
                                        <td className="table__text--align"> {item.onhireDateFormatted}</td>
                                        <td className="table__text--align">
                                            <input type='date' id="onhireDate" name="onhireDate" className={item.type == 'Install' ? 'form-control gridscheduledatesize' : 'hidden'} value={item.onhireDate} onChange={(e) => this.onCellChange(index, "onhireDate", e.target.value)}></input>
                                        </td>
                                        <td className="table__text--align">
                                            <input type='number' min={1} step={1} className="form-control allocate__qtySize" value={item.quantity} onChange={(e) => this.onCellChange(index, "quantity", e.target.value)} />
                                        </td>
                                        <td className="table__text--align">{item.unitOfMeasure}</td>
                                        <td className="table__text--align">{item.priceFormatted}</td>
                                        <td className="table__text--align">{item.overhireRateFormatted}</td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className={(item.quantityTotal - item.quantity == 0) || item.type == 'Install' ? "hidden" : "input-control"} checked={item.rescheduleLater} onChange={(e) => this.onCellChange(index, "rescheduleLater", e.target.checked)} />
                                        </td>
                                        <td className="table__text--align">
                                            <a className={item.notes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, index)}>
                                                <div className="tooltipitem tooltipitem__left">{item.notes ? item.notes.substring(0, 20) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__left">{item.notes}</span>
                                                </div>
                                            </a>
                                            <a className={item.notes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, index)}>
                                                <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                        <td className="table__text--align">
                                            <input type='checkbox' className="input-control" checked={item.pickupOnly} onChange={(e) => this.onCellChange(index, "pickupOnly", e.target.checked)} />
                                        </td>
                                        <td>
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => this.cancelItem(e, index)}>
                                                    <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                </a>
                                            </div>
                                        </td>

                                    </tr>

                                )}
                            </tbody>
                        </table>
                        {this.state.errors["rescheduleItems"] ?
                            (this.state.errors["rescheduleItems"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                        {warnings.map((item, index) => 
                            <span className="label errors" key={index}>{'Warning: ' + DateHelpers.formatDate(item.scheduleDate) + ' is ' + (item.locked ? ' locked. Please unlock day or selected another date' : ' a public holiday or within a shutdown period')}<br /></span> 
                        )}
                    </div>
                    <div className="defaultbutton__container--right">
                        <button className="defaultbutton" type="submit">Reschedule</button>
                    </div>
                </div>
                <div>
                    <div className="row">
                        <hr></hr>
                    </div>
                    <h3>Related Items</h3>
                    <div>
                        <div className="table__margin overflowAuto">
                            <table className="table table__large tableColours">
                                <thead>
                                    <tr>
                                        <th className="hidden">Id</th>
                                        <th></th>
                                        <th>Schedule Date</th>
                                        <th>Status</th>
                                        <th>Site Address</th>
                                        <th>Item</th>
                                        <th>Type</th>
                                        <th>Onhire Date</th>
                                        <th>Quantity</th>
                                        <th>Price</th>
                                        <th>O/H Rate</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scheduledItems.map((item, index) =>
                                        <tr key={index}>
                                            <td className="hidden">{item.id}</td>
                                            <td className="table__text--align">{index + 1}</td>
                                            <td className="table__text--align">{item.scheduledDateFormatted}</td>
                                            <td className="table__text--align">{item.status}</td>
                                            <td className="table__text--align">
                                                <div className="tooltipitem tooltipitem__right">{item.siteAddress ? item.siteAddress.substring(0, 20) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__right">{item.siteAddress}</span>
                                                </div>
                                            </td>
                                            <td className="table__text--align">{item.item}</td>
                                            <td className="table__text--align">{item.type}</td>
                                            <td className="table__text--align">
                                                <input type='date' id="onhireDate" name="onhireDate" className={item.type == 'Install' ? 'form-control gridscheduledatesize' : 'hidden'} value={item.onhireDateFormatted} onChange={(e) => this.onCellChangeRelated(index, "onhireDateFormatted", e.target.value)}></input>
                                            </td>
                                            <td className="table__text--align">{item.quantityScheduled} {item.unitOfMeasure}</td>
                                            <td className="table__text--align">{item.priceFormatted}</td>
                                            <td className="table__text--align">{item.overhireRateFormatted}</td>
                                            <td className="table__text--align">
                                                <a className={item.notes == "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, index)}>
                                                    <div className="tooltipitem tooltipitem__left">{item.notes ? item.notes.substring(0, 20) + "... " : ""}
                                                        <span className="tooltipitemtext tooltipitemtext__left">{item.notes}</span>
                                                    </div>
                                                </a>
                                                <a className={item.notes != "" ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, index)}>
                                                    <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                                </a>
                                            </td>
                                        </tr>

                                    )}
                                </tbody>
                            </table>
                            {this.state.errors["scheduleItems"] ?
                                (this.state.errors["scheduleItems"]).split("\n").map((item: any, key: any) => {
                                    return <span className="label errors" key={key}>{item}<br /></span>
                                })
                                : ""}
                        </div>
                    </div>
                    <div className="defaultbutton__container--right">
                        <button className="defaultbutton" type="button" onClick={this.save}>Save</button>
                    </div>
                </div>
            </form>
        );

    }

};